<template>
	<div v-if="event && (showIconList || showMap)" :class="{ 'has-map': showMap }">

		<div v-if="showIconList" class="grid gap-12">
			<!-- Date & time -->
			<div v-if="eventStart" class="flex-top gap-8 fs-14 lh-20">
				<svg class="block no-shrink" xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="none" viewBox="0 0 20 20"><path stroke="#004ADB" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.25" d="M16.25 3.125H3.75C3.4048 3.125 3.125 3.4048 3.125 3.75V16.25C3.125 16.5952 3.4048 16.875 3.75 16.875H16.25C16.5952 16.875 16.875 16.5952 16.875 16.25V3.75C16.875 3.4048 16.5952 3.125 16.25 3.125ZM13.75 1.875V4.375M6.25 1.875V4.375"/><path stroke="#140047" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.25" d="M3.125 6.875H16.875"/></svg>
				<div class="flex wrap">
					<time class="event-date" :datetime="eventStart.toISODate()">
						{{ eventDate }}
					</time>

					<div v-if="eventStartTime" class="op-5 mx-8">|</div>
					<!-- TODO: display previous time. but only if time as actually set and wasn't left blank -->
					<time :class="{'color-green fw-500': hasChangedTime}" class="event-time event-time-start" :datetime="eventStart.toISOTime()">{{eventStartTime}}</time>
					<template v-if="eventEnd && eventEndTime">
						<span :class="{'color-green fw-500': hasChangedTime}" class="event-time-separator">-</span>
						<time :class="{'color-green fw-500': hasChangedTime}" class="event-time event-time-end" :datetime="eventEnd.toISOTime()">{{eventEndTime}}</time>
					</template>
					<span v-if="hasChangedTime" class="strikethrough">
						<time v-if="previousStartTime">{{ previousStartTime }}</time>
						<span v-if="previousEndTime"> - </span>
						<time v-if="previousEndTime">{{ previousEndTime }}</time>
					</span>
				</div>
			</div>

			<!-- Paid hours -->
			<div v-if="eventMinimumPaidHoursText" class="flex-top gap-8 fs-14 lh-20">
				<svg class="block no-shrink" xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="none" viewBox="0 0 20 20"><g stroke-linecap="round" stroke-width="1.5" clip-path="url(#clip0_5963_67374)"><path stroke="#004ADB" stroke-miterlimit="10" d="M17.9045 9.33093C17.9045 5.15032 14.5115 1.75735 10.3309 1.75735C6.15028 1.75735 2.75732 5.15032 2.75732 9.33093C2.75732 13.5115 6.15028 16.9045 10.3309 16.9045"/><path stroke="#004ADB" stroke-linejoin="round" d="M6.29175 9.33093H10.7147V4.91806"/><path stroke="#140047" stroke-linejoin="round" d="M14.4712 9.1795V17.7426M16.7837 11.8151C16.7837 11.603 16.7433 11.3808 16.6625 11.189 16.5817 10.987 16.4605 10.8053 16.3091 10.6538 16.1576 10.5023 15.9758 10.3811 15.7739 10.3003 15.5719 10.2196 15.3598 10.1792 15.1478 10.1792H13.6634C13.2291 10.1792 12.805 10.3508 12.5021 10.6639 12.189 10.9769 12.0174 11.3909 12.0174 11.8252 12.0174 12.2594 12.189 12.6835 12.5021 12.9864 12.8151 13.2995 13.2291 13.4711 13.6634 13.4711H15.4709C15.9051 13.4711 16.3293 13.6428 16.6322 13.9559 16.9452 14.2689 17.1169 14.6829 17.1169 15.1171 17.1169 15.5514 16.9452 15.9755 16.6322 16.2784 16.3192 16.5915 15.9051 16.7631 15.4709 16.7631H13.4917C13.0575 16.7631 12.6334 16.5915 12.3304 16.2784 12.0174 15.9654 11.8457 15.5514 11.8457 15.1171"/></g><defs><clipPath id="clip0_5963_67374"><rect width="16.6619" height="17.5" fill="#fff" transform="translate(2 1)"/></clipPath></defs></svg>
				<template v-if="hasChangedMinimumPaidHours">
					<span class="color-green fw-500">{{ eventMinimumPaidHoursText }}</span>
					<span class="rel nowrap">
						<span class="abs-center-y left-0 right-0 border-bottom bw-2"></span>
						{{ previousMinimumPaidHoursText.split('of ')[1] }}</span>
				</template>
				<template v-else>
					{{ eventMinimumPaidHoursText }}
				</template>
			</div>

			<!-- Location -->
			<div v-if="locationRecords.length" class="flex-top gap-8 fs-14 lh-20">
				<svg class="block no-shrink" xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="none" viewBox="0 0 20 20"><path stroke="#140047" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M4.375 18.125H15.625"/><path stroke="#004ADB" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M10 10.625C11.3807 10.625 12.5 9.50571 12.5 8.125C12.5 6.74429 11.3807 5.625 10 5.625C8.61929 5.625 7.5 6.74429 7.5 8.125C7.5 9.50571 8.61929 10.625 10 10.625Z"/><path stroke="#004ADB" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M16.25 8.125C16.25 13.75 10 18.125 10 18.125C10 18.125 3.75 13.75 3.75 8.125C3.75 6.4674 4.40848 4.87769 5.58058 3.70558C6.75269 2.53348 8.3424 1.875 10 1.875C11.6576 1.875 13.2473 2.53348 14.4194 3.70558C15.5915 4.87769 16.25 6.4674 16.25 8.125V8.125Z"/></svg>
				
				<div class="grid gap-4">
					<div v-for="(record, i) in locationRecords" :key="`location${i}`">
						<template v-if="hasChangedLocations">
							<template v-if="previous.locations.length >= i + 1">
								<span :class="{'fw-500 color-green': previous.locations[i].address !== record.location.address }">{{ record.location.address }}</span>
								<span v-if="previous.locations[i].address !== record.location.address" class="strikethrough">
									{{ previous.locations[i].address }}
								</span>
							</template>
							<span v-else :class="{'fw-500 green': previous.locations[i]}">{{ record.location.address }}</span>
						</template>
						<template v-else>
							{{ record.location.address }}
						</template>
					</div>
				</div>
			</div>

			<!-- Type -->
			<div v-if="eventTypeName" class="flex-top gap-8 fs-14 lh-20">
				<svg class="block no-shrink" xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="none" viewBox="0 0 20 20"><path stroke="#004ADB" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M16.8755 5.625H3.1255C2.7803 5.625 2.5005 5.9048 2.5005 6.25V16.25C2.5005 16.5952 2.7803 16.875 3.1255 16.875H16.8755C17.2207 16.875 17.5005 16.5952 17.5005 16.25V6.25C17.5005 5.9048 17.2207 5.625 16.8755 5.625ZM13.125 5.625V4.375C13.125 4.0435 12.9933 3.7255 12.7589 3.4911 12.5245 3.2567 12.2065 3.125 11.875 3.125H8.125C7.7935 3.125 7.4755 3.2567 7.2411 3.4911 7.0067 3.7255 6.875 4.0435 6.875 4.375V5.625"/><path stroke="#140047" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M17.5006 9.8679C15.2211 11.1867 12.6334 11.8791 10 11.875 7.367 11.8791 4.7797 11.1869 2.5005 9.8686M9.0625 9.375H10.9375"/></svg>
				{{ eventTypeName }}
			</div>

			<!-- Note -->
			<template v-if="!hideNotes && notes">
				<div class="flex-top gap-8 fs-14 lh-20">
					<svg class="block no-shrink" xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="none" viewBox="0 0 20 20"><path stroke="#140047" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M7.5 7.5H12.5M7.5 10H12.5M7.5 12.5H10"/><path stroke="#004ADB" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M12.2411 16.875H3.75C3.58424 16.875 3.42527 16.8092 3.30806 16.6919C3.19085 16.5747 3.125 16.4158 3.125 16.25V3.75C3.125 3.58424 3.19085 3.42527 3.30806 3.30806C3.42527 3.19085 3.58424 3.125 3.75 3.125H16.25C16.4158 3.125 16.5747 3.19085 16.6919 3.30806C16.8092 3.42527 16.875 3.58424 16.875 3.75V12.2411C16.875 12.3232 16.8588 12.4045 16.8274 12.4803C16.796 12.5561 16.75 12.625 16.6919 12.6831L12.6831 16.6919C12.625 16.75 12.5561 16.796 12.4803 16.8274C12.4045 16.8588 12.3232 16.875 12.2411 16.875V16.875Z"/><path stroke="#004ADB" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M16.8184 12.4994H12.5V16.8181"/></svg>
					Job Note
				</div>
				<textarea v-model="event.notes" class="fs-14 lh-18 pad-16" readonly style="height:88px;"></textarea>
			</template>
		</div>
		
		<div v-if="candidateStatus && candidateStatus.status == 'awaitingChangesResponse' && !hasAcceptedChanges" class="flex wrap gap-12 mt-24">
			<button class="btn btn-green" @click.stop="acceptChanges">
				<icon-check-slim width="12"/>
				Accept Changes
			</button>
			<button class="btn btn-hover-red" @click.stop="declineJob">
				<icon-x />Decline Job
			</button>
		</div>

		<div v-if="showMap" class="map event-map br-8" ref="map"></div>
	</div>
</template>

<script>
import MarkerClusterer from '@googlemaps/markerclustererplus';
import { DateTime } from 'luxon';
import { toParagraphs } from '@/helpers/convert';
import { getGoogleMapsSearchUrl } from '@/helpers/geo';
import { formatEventEndTime, formatEventStartTime } from '@/helpers/jobs';
import { acceptJobChanges } from '@/services/swm/api';

import JobEvent from '@/models/JobEvent';

export default {
	name: 'EventDetailsNew',
	model: {
		prop: 'event'
	},
	props: {
		jobRole: {
			required: false,
			default: false,
		},
		event: {
			type: JobEvent,
			default: null,
		},
		hideMap: {
			type: Boolean,
			default: false,
		},
		hideNotes: {
			type: Boolean,
			default: false,
		},
		showChanges: {
			type: Boolean,
			default: false,
		},
		isEditing: {
			type: Boolean,
			default: false,
		},
	},
	data() {
		return {
			hasAcceptedChanges: false,
		};
	},
	computed: {
		job() {
			return this.$store.getters['singleJob/job'];
		},
		hasPendingChanges() {
			// TODO: instant offer status still 'accepted' instead of 'awaitingChangesResponse' when changes
			if (this.event.updateHistories.length && this.candidateStatus && this.candidateStatus.status == 'awaitingChangesResponse') {
				return true;
			}
			return false;
		},
		eventDate() {
			return this.formatEventDate(this.eventStart);
		},
		eventEnd() {
			return this.$toDateTime(this.event?.endedAt, {
				zone: this.eventTimeZone
			});
		},
		eventEndTime() {
			return formatEventEndTime(this.eventEnd);
		},
		eventMinimumPaidHoursText() {
			return this.getMinimumPaidHoursText(this.event?.minimumPaidHours);
		},
		eventStart() {
			return this.$toDateTime(this.event?.startedAt, {
				zone: this.eventTimeZone
			});
		},
		eventStartTime() {
			return formatEventStartTime(this.eventStart);
		},
		eventTimeZone() {
			return this.getEventTimeZone(this.event);
		},
		eventTypeName() {
			return this.event?.type?.name || null;
		},
		filteredLocations() {
			const event = this.event;
			const filteredLocations = [];
			let locations;
			if (event && (locations = event.locations) && locations.length > 0) {
				const addresses = new Set();
				let address;
				let i = 0;
				for (const location of locations) {
					if (
						(address = location.address) &&
						!addresses.has((address = address.trim().toLowerCase()))
					) {
						addresses.add(address);
						filteredLocations[i++] = location;
					}
				}
			}
			return filteredLocations;
		},
		hasChangedLocations() {
			return this.locationRecords.some((record) => record.editType);
		},
		hasChangedMinimumPaidHours() {
			return !!(
				this.previous && this.previousMinimumPaidHoursText !== this.eventMinimumPaidHoursText
			);
		},
		hasChangedTime() {
			return !!(
				this.previous &&
				(this.previousStartTime !== this.eventStartTime ||
					this.previousEndTime !== this.eventEndTime)
			);
		},
		locationRecords() {
			const filteredLocations = this.filteredLocations;
			const records = filteredLocations.map((location) => ({
				editType: null,
				location,
				previousAddress: null,
				url: getGoogleMapsSearchUrl(location.address),
			}));

			const previous = this.previous;
			if (previous) {
				const previousLocations = new Map(
					previous.locations.map((location) => [location.address, location])
				);
				const remainingRecords = new Set(records);
				for (const record of records) {
					const address = record.location.address;
					const match = previousLocations.get(address);
					if (match) {
						previousLocations.delete(address);
						remainingRecords.delete(record);
					}
				}
				const remainingPreviousLocations = Array.from(previousLocations.values());
				let i = 0;
				for (const record of remainingRecords) {
					if (i < remainingPreviousLocations.length) {
						record.editType = 'changed';
						record.previousAddress = remainingPreviousLocations[i++].address;
					} else {
						record.editType = 'added';
					}
				}
				for (const location of remainingPreviousLocations.slice(i)) {
					records.push({
						editType: 'removed',
						location,
						previousAddress: null,
						url: null,
					});
				}
			}

			return records;
		},
		mappedLocations() {
			const filteredLocations = this.filteredLocations;
			return filteredLocations.filter((location) => location.hasCoordinates());
		},
		notes() {
			return toParagraphs(this.event?.notes);
		},
		previous() {
			let event;
			if (this.hasAcceptedChanges) {
				return null;
			}
			return (this.hasPendingChanges && (event = this.event) && event.updateHistories[0]) || null;
		},
		previousEnd() {
			return this.$toDateTime(this.previous?.endedAt, {
				zone: this.previousTimeZone
			});
		},
		previousEndTime() {
			return formatEventEndTime(this.previousEnd);
		},
		previousMinimumPaidHoursText() {
			return this.getMinimumPaidHoursText(this.previous?.minimumPaidHours);
		},
		previousStart() {
			return this.$toDateTime(this.previous?.startedAt, {
				zone: this.previousTimeZone
			});
		},
		previousStartTime() {
			return formatEventStartTime(this.previousStart);
		},
		previousTimeZone() {
			return this.getEventTimeZone(this.previous);
		},
		showIconList() {
			if (this.isEditing) return false;
			
			return !!(
				this.eventStart ||
				this.hasChangedDate ||
				this.hasChangedTime ||
				this.locationRecords.length ||
				this.eventMinimumPaidHoursText ||
				this.hasChangedMinimumPaidHours ||
				this.jobRole ||
				(this.notes && !this.hideNotes)
			);
		},
		showMap() {
			return !this.hideMap && this.mappedLocations.length > 0;
		},
		candidateStatus() {
			return this.$store.getters['singleJob/candidateStatus'];
		},
	},
	methods: {
		declineJob() {
			this.$store.commit('jobs/setDecliningJob', this.job);
		},
		async acceptChanges() {
			try {
				await acceptJobChanges({offerId: this.candidateStatus.offer.id});
				this.hasAcceptedChanges = true;
				this.$emit('acceptChanges');
			} catch (error) {
				console.log('@@ acceptJobChanges error', error);
			}
		},
		async clearMap() {
			const { infoWindows, map, markerClusterer, markers } = this;
			if (!map || !markerClusterer) {
				return;
			}
			const gmaps = await this.$getGoogleMaps();
			for (const infoWindow of infoWindows) {
				infoWindow.close();
				gmaps.event.clearInstanceListeners(infoWindow);
			}
			markerClusterer.removeMarkers(markers);
			infoWindows.length = 0;
			markers.length = 0;
		},
		formatEventDate(date, timeZone) {
			return date
				? (timeZone ? date.setZone(timeZone) : date).toLocaleString(DateTime.DATE_HUGE)
				: null;
		},
		getEventTimeZone(event) {
			return event?.timeZone || this.$localTimeZone;
		},
		getMinimumPaidHoursText(hours) {
			return hours ? `Minimum of ${hours} paid hour${hours === 1 ? '' : 's'}` : '';
		},
		async initMap() {
			const container = this.$refs.map;
			if (!this.showMap || !container) {
				return null;
			}
			
			let result = this.map;
			if (!result) {
				result = this.$getGoogleMaps().then(async (gmaps) => {
					const map = new gmaps.Map(container, {
					center: { lat: 37.7596324, lng: -95.6903276 },
					disableDefaultUI: true,
					styles: [
					{
						featureType: 'poi.business',
						stylers: [{ visibility: 'off' }],
					},
					],
					zoom: 1,
				});
          
				const markerClusterer = new MarkerClusterer(map, [], {
					imageSizes: [mapIconSize, mapIconSize, mapIconSize, mapIconSize, mapIconSize],
				});
          
				for (const style of markerClusterer.getStyles()) {
					style.url = require('@/assets/icons/pin.svg');
				}
          
				Object.defineProperties(this, {
					map: {
						configurable: false,
						value: map,
					},
					markerClusterer: {
						configurable: false,
						value: markerClusterer,
					},
				});
				
				await this.populateMap();
					return map;
				});
				
				Object.defineProperty(this, 'map', {
					configurable: true,
					value: result,
				});
			}

			return result;
		},
		async populateMap() {
      const { infoWindows, map, mappedLocations, markerClusterer, markers } = this;
      if (!this.showMap || !map || map instanceof Promise || !markerClusterer) {
        return;
      }

      const [gmaps] = await Promise.all([this.$getGoogleMaps(), this.clearMap()]);
      const iconSize = new gmaps.Size(mapIconSize, mapIconSize);
      let i = 0;

      for (const location of mappedLocations) {
        /** @type {URL | null} */
        const url = getGoogleMapsSearchUrl(location.address);
        const marker = new gmaps.Marker({
          collisionBehavior: gmaps.CollisionBehavior?.REQUIRED_AND_HIDES_OPTIONAL,
          icon: {
            scaledSize: iconSize,
            size: iconSize,
            url: require('@/assets/icons/pin.svg'),
          },
          position: {
            lat: location.latitude,
            lng: location.longitude,
          },
        });
        const infoWindow = new gmaps.InfoWindow({
          content: `
<div class="event-map-location">
<address class="event-map-location-address">${location.address}</address>
${
  url
    ? `
  <a href="${url}" class="event-map-location-link button button-inline button-icon button-icon-share button-icon-right" target="_blank" rel="external noopener">
    <img src="${require('@/assets/icons/share.svg')}" class="icon icon-share" width="16" height="16" />
    Open in Google Maps
  </a>`
    : ''
}
</div>
`,
        });
        marker.addListener('click', this.showInfoWindow.bind(this, infoWindow, marker));
        infoWindows[i] = infoWindow;
        markers[i] = marker;
        i++;
      }

      markerClusterer.addMarkers(markers);
      markerClusterer.fitMapToMarkers(80);
      map.addListener('idle', () => {
        const zoom = map.getZoom();
        if (zoom != null && zoom >= 15) {
          map.setZoom(15);
        }
      });
    },
    /**
     * @param {google.maps.InfoWindow} infoWindow
     * @param {google.maps.Marker} marker
     * @returns {void}
     */
    showInfoWindow(infoWindow, marker) {
      this.infoWindows.forEach((otherWindow) => otherWindow.close());
      infoWindow.open(this.map, marker);
    },
  },
  created() {
    // The properties below are intentionally non-reactive.
    Object.defineProperties(this, {
      infoWindows: {
        value: [],
      },
      map: {
        configurable: true,
        value: null,
      },
      markerClusterer: {
        configurable: true,
        value: null,
      },
      markers: {
        value: [],
      },
    });
  },
  mounted() {
    this.initMap();
  },
  updated() {
    this.initMap();
  },
  watch: {
    mappedLocations: 'populateMap',
  },
};

const mapIconSize = 32;

/**
 * @typedef {LocationRecord[]} LocationRecords
 */

/**
 * @typedef {{
 *   editType: 'added' | 'changed' | 'removed' | null;
 *   location: JobEventLocation & { address: string; };
 *   previousAddress: string | null;
 *   url: URL | null;
 * }} LocationRecord
 */

/**
 * @typedef {LocationRecord & {
 *   location: LocationRecord['location'] & { latitude: number; longitude: number; };
 * }} LocationRecordWithCoordinates
 */
</script>
